import { useEffect, useRef } from "react"
import { useDaily, useTranscription } from "./callTransport"

/** How long to wait after entering a room before starting its transcription (lets Daily settle). */
export const BREAKOUT_TRANSCRIPTION_DELAY_MS = 3000

/**
 * Breakout rooms have no owner to auto-start transcription, so the first attendee in a room
 * starts it (their room token carries transcription admin rights). A room that is already
 * transcribing is left alone; the host's owner token auto-starts it when they hop in.
 *
 * The same applies to the main room of an administrator-scheduled 1:1 whose host never joins:
 * the server grants the invited pair the transcription permission (`startInMain`), and this
 * hook starts transcription if Daily has not already done so from the token.
 */
export function useBreakoutTranscription({ inRoom, enabled, startInMain = false, roomId, canStart }: { inRoom: boolean; enabled: boolean; startInMain?: boolean; roomId?: string; canStart?: () => boolean }) {
  const daily = useDaily()
  const { isTranscribing } = useTranscription()
  const attemptedRef = useRef(false)
  const canStartRef = useRef(canStart)
  canStartRef.current = canStart
  const scope = inRoom ? `room:${roomId || ""}` : startInMain ? "main" : ""

  useEffect(() => {
    attemptedRef.current = false
  }, [scope])

  useEffect(() => {
    if (!scope || !enabled || !daily || isTranscribing || attemptedRef.current) return
    const timer = window.setTimeout(() => {
      if (canStartRef.current && !canStartRef.current()) return
      attemptedRef.current = true
      try {
        daily.startTranscription()
      } catch {
        // Another attendee may have started it first, or the token lacks the permission.
      }
    }, BREAKOUT_TRANSCRIPTION_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [daily, enabled, scope, isTranscribing])
}
